import { useNavigate } from 'react-router-dom';
import type { InboxPcoFacet, InboxPdf } from '@coffman-tm/shared';
import { formatDateShort } from '../../lib/format';
import type { InboxForemanFacet } from '../../lib/useInbox';
import { Dropdown, SkeletonRows } from '../design';
import { HelpTip } from '../HelpTip';
import { buildForemanFilterOptions, buildPcoFilterOptions } from './inboxFilterOptions';

/**
 * Master pane of the PDF Inbox — a scrollable list of PDFs awaiting review.
 * One row per PDF, stripped to foreman + report date + a single status chip.
 * The whole row navigates to /inbox/:dailyPdfId; the detail pane (the route's
 * <Outlet> child) renders the selected PDF's blocks. Selection is driven by
 * the URL, passed in as `selectedId`.
 *
 * The row leads with the FOREMAN, not the filename. Filenames here are
 * derived from the HeavyJob email subject ("NG 6.15.26 - 4.13 t&m 6.20.26.pdf")
 * — near-identical across rows, truncated by the narrow pane, and useless for
 * picking the right report out of a day's stack. Who filed it and when is what
 * Joey actually scans for. The filename stays the detail pane's title (and the
 * row's hover tooltip), so nothing is lost.
 */
export interface PdfInboxListProps {
  pdfs: InboxPdf[];
  isLoading: boolean;
  error: Error | null;
  /** daily_pdf_id of the currently routed detail, or null at "/". */
  selectedId: string | null;
  /** Per-PCO pending-block breakdown for the PCO filter select. Empty until
   *  the API ships `pco_facets` — see useInbox. */
  pcoFacets: InboxPcoFacet[];
  /** Count of pending blocks with no effective PCO (the "Unassigned" PCO
   *  option). 0 until the API ships it. */
  unassignedCount: number;
  /** Per-foreman pending-block breakdown for the Foreman filter select.
   *  Empty until the API ships `foreman_facets`. */
  foremanFacets: InboxForemanFacet[];
  /** Currently-applied PCO filter — a pco_id, `'unassigned'`, or null for
   *  "All". Lives in the parent's component state only — never persisted. */
  selectedPcoId: string | null;
  /** Currently-applied foreman filter — exact name, or null for "All". */
  selectedForeman: string | null;
  onSelectPco: (pcoId: string | null) => void;
  onSelectForeman: (foreman: string | null) => void;
  /** Decided PDFs the work pile hides that still carry a skipped block. 0
   *  until the API ships it, and 0 whenever a filter is applied — either way
   *  the disclosure doesn't render. */
  skippedCount: number;
  /** The rows behind that count — empty unless `showSkipped`. */
  skippedPdfs: InboxPdf[];
  showSkipped: boolean;
  onToggleSkipped: () => void;
}

export function PdfInboxList({
  pdfs,
  isLoading,
  error,
  selectedId,
  pcoFacets,
  unassignedCount,
  foremanFacets,
  selectedPcoId,
  selectedForeman,
  onSelectPco,
  onSelectForeman,
  skippedCount,
  skippedPdfs,
  showSkipped,
  onToggleSkipped,
}: PdfInboxListProps) {
  const navigate = useNavigate();
  const pcoOptions = buildPcoFilterOptions(pcoFacets, unassignedCount);
  const foremanOptions = buildForemanFilterOptions(foremanFacets);

  return (
    <>
      <div className="inbox-master-head">
        <h1>
          PDF Inbox
          <HelpTip title="Your review queue" learnMore="inbox">
            Foreman PDFs land here for review. Confirm or edit each block, then route to a PCO.
          </HelpTip>
        </h1>
      </div>

      {/* A dimension with no options (facets empty/absent) renders nothing —
          a filter with one choice is noise, not a control. */}
      {(pcoOptions.length > 0 || foremanOptions.length > 0) && (
        <InboxFilterBar
          pcoOptions={pcoOptions}
          foremanOptions={foremanOptions}
          selectedPcoId={selectedPcoId}
          selectedForeman={selectedForeman}
          onSelectPco={onSelectPco}
          onSelectForeman={onSelectForeman}
        />
      )}

      {isLoading && pdfs.length === 0 ? (
        <SkeletonRows rows={6} />
      ) : error ? (
        <div style={{ color: 'var(--danger)', fontSize: 13, padding: '12px var(--pad-x)' }}>
          Failed to load inbox: {error.message}
        </div>
      ) : (
        <>
          {pdfs.length === 0 ? (
            <div
              className="muted"
              style={{ fontSize: 13, padding: '16px var(--pad-x)', lineHeight: 1.5 }}
            >
              No PDFs waiting for review. New ones land here as foremen forward them.
            </div>
          ) : (
            <div className="inbox-list">
              {pdfs.map((pdf) => (
                <InboxRow
                  key={pdf.daily_pdf_id}
                  pdf={pdf}
                  selected={pdf.daily_pdf_id === selectedId}
                  onOpen={() => navigate(`/inbox/${pdf.daily_pdf_id}`)}
                />
              ))}
            </div>
          )}

          {/* SKIPPED REPORTS — the one decision with no other surface.
              A saved block is readable from its PCO's week and an ignored one
              is reversed at its code in Settings; a skipped block's only
              control is the Re-open on its card, and the card went out of
              reach the moment its PDF had no pending blocks left. Same quiet
              disclosure the Settings user list uses for removed accounts
              (SettingsPage `N removed accounts — show`): a count and a verb,
              at the foot of the working list, collapsed on every load. */}
          {/* No rule of its own: the last row above already carries a bottom
              border, and two would read as an empty row (RULES 8.4). */}
          {skippedCount > 0 && (
            <div style={{ padding: '10px var(--pad-x)' }}>
              <button
                type="button"
                className="btn-link muted"
                style={{ fontSize: 12 }}
                aria-expanded={showSkipped}
                onClick={onToggleSkipped}
              >
                {skippedCount} skipped report{skippedCount === 1 ? '' : 's'} —{' '}
                {showSkipped ? 'hide' : 'show'}
              </button>
            </div>
          )}
          {showSkipped && skippedPdfs.length > 0 && (
            <div className="inbox-list">
              {skippedPdfs.map((pdf) => (
                <InboxRow
                  key={pdf.daily_pdf_id}
                  pdf={pdf}
                  selected={pdf.daily_pdf_id === selectedId}
                  onOpen={() => navigate(`/inbox/${pdf.daily_pdf_id}`)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * One PDF row. Identical for the work pile and for a revealed skipped report —
 * the chip is what says which it is, so a revealed row can never read as work
 * waiting.
 */
function InboxRow({
  pdf,
  selected,
  onOpen,
}: {
  pdf: InboxPdf;
  selected: boolean;
  onOpen: () => void;
}) {
  // No foreman parsed off the PDF: fall back to the filename as the headline
  // rather than rendering a blank row.
  const title = pdf.foreman_name ?? pdf.filename;
  const sub = pdf.report_date ? formatDateShort(pdf.report_date) : '';
  return (
    <button
      type="button"
      className={`inbox-row${selected ? ' selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
      title={pdf.filename}
      onClick={onOpen}
    >
      <span className="inbox-row-main">
        <span className="inbox-row-name">{title}</span>
        <span className="inbox-row-sub">{sub}</span>
        {/* A second AI model re-read this report and disagreed with what was
            recorded. Advisory: it blocks nothing here, and the send window is
            where it has to be acknowledged. */}
        {pdf.verify_status === 'flagged' && (
          <span className="inbox-row-verify" title={pdf.verify_summary ?? undefined}>
            Re-read disagrees
          </span>
        )}
      </span>
      <RowStatusChip pdf={pdf} />
      <span className="inbox-row-chevron" aria-hidden>
        <ChevronRight />
      </span>
    </button>
  );
}

/**
 * Single status indicator per row. Pending work shows as a small red count
 * circle (just the number — quiet, not the old loud "N PENDING" pill); the
 * tooltip spells out what the number means. A fully-processed PDF reads as a
 * quiet green "Reviewed". Display-only — the row itself is the click target.
 */
function RowStatusChip({ pdf }: { pdf: InboxPdf }) {
  if (pdf.blocks_pending > 0) {
    return (
      <span
        className="inbox-count-badge"
        title={`${pdf.blocks_pending} block${pdf.blocks_pending === 1 ? '' : 's'} pending review`}
      >
        {pdf.blocks_pending}
      </span>
    );
  }
  // Nothing on this report bills. Green "Reviewed" would be the wrong claim on
  // a row the PM opened precisely because he wanted the work back — so it
  // takes the same muted grey the block-level Skipped pill uses, and says the
  // word. A report with even one saved block still reads Reviewed: some of it
  // billed, which is what that chip has always meant.
  if (pdf.blocks_saved === 0 && pdf.blocks_skipped > 0) {
    return (
      <span
        className="status-chip s-skipped"
        title={`${pdf.blocks_skipped} block${pdf.blocks_skipped === 1 ? '' : 's'} skipped — nothing on this report bills`}
      >
        Skipped
      </span>
    );
  }
  // The per-PDF undecided-material count used to produce a second red count
  // here, on a PDF whose blocks were all decided. Removed 2026-08-03 (M12): a
  // parser material candidate is not review work, so a report with no pending
  // blocks is Reviewed, full stop.
  return <span className="status-chip s-saved">Reviewed</span>;
}

/**
 * PCO + Foreman filter bar for the master list header. Two full-width selects
 * stacked on a fixed grid; the dimension words live INSIDE each control's
 * "All" option ("All PCOs (n)" / "All foremen (n)"), not in eyebrow labels
 * beside it — in this ~280px column, label-left gave each select a different
 * left edge, which is what read as clutter (2026-08-06). Each narrowed select
 * carries its own ✕ inside the control (Dropdown `onClear`), which clears
 * exactly the filter it sits in — same effect as picking its All option.
 */
function InboxFilterBar({
  pcoOptions,
  foremanOptions,
  selectedPcoId,
  selectedForeman,
  onSelectPco,
  onSelectForeman,
}: {
  pcoOptions: ReturnType<typeof buildPcoFilterOptions>;
  foremanOptions: ReturnType<typeof buildForemanFilterOptions>;
  selectedPcoId: string | null;
  selectedForeman: string | null;
  onSelectPco: (pcoId: string | null) => void;
  onSelectForeman: (foreman: string | null) => void;
}) {
  // Filters compose (AND), but each ✕ scopes to the control it sits inside —
  // clearing "this select" is what the affordance visibly promises.
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        rowGap: 6,
        padding: '8px var(--pad-x)',
        borderBottom: '1px solid var(--rule)',
        background: 'var(--surface)',
      }}
    >
      {pcoOptions.length > 0 && (
        <FilterSelect
          label="PCO"
          value={selectedPcoId ?? ''}
          options={pcoOptions}
          active={selectedPcoId !== null}
          onChange={(v) => onSelectPco(v === '' ? null : v)}
        />
      )}
      {foremanOptions.length > 0 && (
        <FilterSelect
          label="Foreman"
          value={selectedForeman ?? ''}
          options={foremanOptions}
          active={selectedForeman !== null}
          onChange={(v) => onSelectForeman(v === '' ? null : v)}
        />
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  active,
  onChange,
}: {
  label: string;
  value: string;
  options: ReturnType<typeof buildPcoFilterOptions>;
  active: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Dropdown
      uiSize="sm"
      value={value}
      // Labels arrive pre-truncated from inboxFilterOptions — the CLOSED
      // control is narrow in a ~280px column, so a full PCO description
      // would be ellipsized to nothing useful either way.
      options={options}
      onChange={onChange}
      aria-label={label}
      style={{ gridColumn: 1, minWidth: 0 }}
      // An engaged filter keeps its accent border — the only way the closed
      // control says "this list is narrowed" once the label scrolls past.
      triggerStyle={active ? { borderColor: 'var(--accent)' } : undefined}
      // The ✕ rides the control only while it is narrowed; clearing is the
      // same '' → null mapping the All option takes through onChange.
      onClear={active ? () => onChange('') : undefined}
      clearLabel={`Clear ${label} filter`}
    />
  );
}

function ChevronRight() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M6 4 L10 8 L6 12"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
